import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';

type FormAutoCompleteProps = {
    label: string;
    name: string;
    value?: string;
    onChange?: (value: string) => void;
    options: string[];
    error?: string;
    placeholder?: string;
    disabled?: boolean;
    menuFixed?: boolean;
};

const FormAutoComplete = ({ label, name, value, onChange, options, error, placeholder, disabled, menuFixed }: FormAutoCompleteProps) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState(value || '');
    const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
    const wrapperRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setSearch(value || '');
    }, [value]);

    const filtered = options.filter(o =>
        o?.toLowerCase().includes(search.toLowerCase())
    );

    const handleSelect = (opt: string) => {
        setSearch(opt);
        onChange?.(opt);
        setOpen(false);
    };

    const handleChange = (val: string) => {
        setSearch(val);
        onChange?.(val);
        setOpen(true);
    };

    const updateMenuPosition = () => {
        if (!menuFixed || !inputRef.current) return;
        const rect = inputRef.current.getBoundingClientRect();
        setMenuStyle({
            position: 'fixed',
            top: rect.bottom + 4,
            left: rect.left,
            width: rect.width,
            zIndex: 9999,
        });
    };

    // Close dropdown on outside click
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            const target = e.target as Node;
            const insideWrapper = wrapperRef.current?.contains(target);
            const insideMenu = menuRef.current?.contains(target);
            if (!insideWrapper && !insideMenu) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const dropdownContent = open && filtered.length > 0 ? (
        <div
            ref={menuRef}
            className={menuFixed ? undefined : 'absolute z-50 mt-1 w-full'}
            style={menuFixed ? {
                ...menuStyle,
                backgroundColor: 'var(--bg-card)',
                borderColor: 'var(--border-default)',
                boxShadow: 'var(--shadow-lg)',
                maxHeight: '12rem',
                overflowY: 'auto',
                borderRadius: '0.5rem',
                border: '1px solid var(--border-default)',
            } : {
                backgroundColor: 'var(--bg-card)',
                borderColor: 'var(--border-default)',
                boxShadow: 'var(--shadow-lg)',
                maxHeight: '12rem',
                overflowY: 'auto',
                borderRadius: '0.5rem',
                border: '1px solid var(--border-default)',
            }}
        >
            {filtered.map((opt, i) => (
                <button
                    key={i}
                    type="button"
                    className="w-full px-3 py-2 text-left text-sm transition-colors"
                    style={{
                        color: 'var(--text-primary)',
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                    }}
                    onMouseOver={(e) => { e.currentTarget.style.backgroundColor = 'var(--bg-hover)'; }}
                    onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                    onClick={() => handleSelect(opt)}
                >
                    {opt}
                </button>
            ))}
        </div>
    ) : null;

    return (
        <div className="mb-4 relative" ref={wrapperRef}>
            <label
                htmlFor={name}
                className="block text-sm font-medium mb-1.5"
                style={{ color: 'var(--text-secondary)' }}
            >
                {label}
            </label>
            <input
                id={name}
                name={name}
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => handleChange(e.target.value)}
                onFocus={() => { updateMenuPosition(); setOpen(true); }}
                placeholder={placeholder}
                disabled={disabled}
                className="input-base"
                autoComplete="off"
            />
            {menuFixed
                ? dropdownContent && ReactDOM.createPortal(dropdownContent, document.body)
                : dropdownContent
            }
            {error && (
                <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{error}</p>
            )}
        </div>
    );
};

export default FormAutoComplete;
